import React, { useState, useEffect } from 'react';
import { readingPlanService } from '../../../services/supabaseService';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { cn } from '../../../utils/cn';

const ProgressTracker = ({ plan, onBack }) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('7');
  const [progressStats, setProgressStats] = useState(null);
  const [upcomingReadings, setUpcomingReadings] = useState([]);
  const [readingStats, setReadingStats] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(0);
  const [completionStatus, setCompletionStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(false);

  useEffect(() => {
    if (plan?.id) {
      fetchAllData();
    }
  }, [plan?.id]);

  useEffect(() => {
    if (plan?.id) {
      loadCalendarData();
    }
  }, [plan?.id, currentMonth]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchProgressStats(),
        fetchUpcomingReadings(),
        fetchReadingStats()
      ]);
    } catch (error) {
      console.error('Error fetching progress data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgressStats = async () => {
    try {
      const stats = await readingPlanService?.getReadingPlanProgressStats(plan?.id);
      setProgressStats(stats);
    } catch (error) {
      console.error('Error fetching progress stats:', error);
    }
  };

  const fetchUpcomingReadings = async () => {
    try {
      const currentDay = plan?.current_day || 1;
      const totalDays = plan?.reading_plans?.duration_days || 365;
      const daysToFetch = Math.min(7, totalDays - currentDay + 1);

      if (daysToFetch <= 0) return;

      // Fetch next N days of readings
      const { data: enrollmentData } = await supabase
        ?.from('user_reading_plans')
        ?.select('reading_plan_id')
        ?.eq('id', plan?.id)
        ?.single();

      if (!enrollmentData?.reading_plan_id) return;

      const dayNumbers = Array.from({ length: daysToFetch }, (_, i) => currentDay + i);

      const { data: daysData } = await supabase
        ?.from('reading_plan_days')
        ?.select(`
          day_number,
          title,
          reading_plan_references (
            reference_text,
            display_order
          )
        `)
        ?.eq('reading_plan_id', enrollmentData?.reading_plan_id)
        ?.in('day_number', dayNumbers)
        ?.order('day_number');

      if (daysData) {
        const readings = daysData?.map((day, idx) => {
          const refs = day?.reading_plan_references
            ?.sort((a, b) => a?.display_order - b?.display_order)
            ?.map(r => r?.reference_text)
            ?.filter(Boolean) || [];

          let dateLabel;
          if (idx === 0) dateLabel = 'Today';
          else if (idx === 1) dateLabel = 'Tomorrow';
          else {
            const d = new Date();
            d?.setDate(d?.getDate() + idx);
            dateLabel = d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }

          return {
            date: dateLabel,
            dayNumber: day?.day_number,
            title: day?.title,
            passages: refs,
            completed: idx === 0 ? false : false
          };
        });
        setUpcomingReadings(readings);
      }
    } catch (error) {
      console.error('Error fetching upcoming readings:', error);
    }
  };

  const fetchReadingStats = async () => {
    try {
      // Get completed references count and other stats from user_reading_progress
      const { data: progressData } = await supabase
        ?.from('user_reading_progress')
        ?.select('id, is_completed, completed_at, reading_plan_references(book_name, chapter, verse_start, verse_end)')
        ?.eq('user_reading_plan_id', plan?.id)
        ?.eq('is_completed', true);

      if (!progressData) return;

      // Calculate stats from real data
      const completedRefs = progressData?.length || 0;

      // Estimate verses read (avg ~26 verses per chapter)
      let versesRead = 0;
      let chaptersCompleted = 0;
      const booksStarted = new Set();
      const booksCompleted = new Set();
      const bookChapterMap = {};

      progressData?.forEach(p => {
        const ref = p?.reading_plan_references;
        if (!ref) return;

        const book = ref?.book_name;
        if (book) {
          booksStarted?.add(book);
          if (!bookChapterMap?.[book]) bookChapterMap[book] = new Set();
          bookChapterMap?.[book]?.add(ref?.chapter);
        }

        if (ref?.verse_start && ref?.verse_end) {
          versesRead += (ref?.verse_end - ref?.verse_start + 1);
          chaptersCompleted++;
        } else if (ref?.chapter) {
          versesRead += 26; // average
          chaptersCompleted++;
        }
      });

      // Books completed = books where we have many chapters (heuristic: 5+ chapters)
      Object.entries(bookChapterMap)?.forEach(([book, chapters]) => {
        if (chapters?.size >= 5) booksCompleted?.add(book);
      });

      // Estimate time: avg 15 min per day completed
      const completedDays = plan?.completed_days || 0;
      const totalMinutes = completedDays * 15;
      const hours = (totalMinutes / 60)?.toFixed(1);
      const avgMinutes = completedDays > 0 ? Math.round(totalMinutes / completedDays) : 0;

      setReadingStats({
        averageTime: avgMinutes > 0 ? `${avgMinutes} minutes` : '—',
        totalTimeSpent: totalMinutes > 0 ? `${hours} hours` : '—',
        versesRead: versesRead > 0 ? versesRead : completedRefs * 26,
        chaptersCompleted: chaptersCompleted > 0 ? chaptersCompleted : completedRefs,
        booksStarted: booksStarted?.size,
        booksCompleted: booksCompleted?.size
      });
    } catch (error) {
      console.error('Error fetching reading stats:', error);
    }
  };

  const loadCalendarData = async () => {
    if (!plan?.id) return;
    setCalendarLoading(true);
    try {
      const startDate = new Date(plan?.start_date || new Date());
      const totalDays = plan?.reading_plans?.duration_days || 365;
      const currentDay = plan?.current_day || 1;

      const displayDate = new Date(startDate);
      displayDate?.setMonth(displayDate?.getMonth() + currentMonth);

      const firstDayOfMonth = new Date(displayDate.getFullYear(), displayDate.getMonth(), 1);
      const lastDayOfMonth = new Date(displayDate.getFullYear(), displayDate.getMonth() + 1, 0);

      const daysInMonth = [];
      for (let date = new Date(firstDayOfMonth); date <= lastDayOfMonth; date?.setDate(date?.getDate() + 1)) {
        const planDay = Math.floor((date - startDate) / (1000 * 60 * 60 * 24)) + 1;
        if (planDay >= 1 && planDay <= totalDays) {
          daysInMonth?.push({
            date: new Date(date),
            planDay,
            monthDay: date?.getDate(),
            isToday: planDay === currentDay,
            isPast: planDay < currentDay,
            isFuture: planDay > currentDay
          });
        } else {
          daysInMonth?.push({
            date: new Date(date),
            planDay: null,
            monthDay: date?.getDate(),
            isToday: false,
            isPast: false,
            isFuture: false
          });
        }
      }

      setCalendarData(daysInMonth);

      // Load completion status
      const planDays = daysInMonth?.filter(d => d?.planDay)?.map(d => d?.planDay);
      if (planDays?.length > 0) {
        const statusMap = {};
        for (const dayNum of planDays) {
          const isComplete = await readingPlanService?.isDayCompleted(plan?.id, dayNum);
          statusMap[dayNum] = isComplete;
        }
        setCompletionStatus(statusMap);
      }
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setCalendarLoading(false);
    }
  };

  const getMonthYear = () => {
    const startDate = new Date(plan?.start_date || new Date());
    const displayDate = new Date(startDate);
    displayDate?.setMonth(displayDate?.getMonth() + currentMonth);
    return displayDate?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getFirstDayOffset = () => {
    if (calendarData?.length === 0) return 0;
    return calendarData?.[0]?.date?.getDay() || 0;
  };

  const overallCompletion = progressStats?.completionPercentage || plan?.progress || 0;
  const totalDays = progressStats?.totalDays || plan?.reading_plans?.duration_days || 365;
  const currentDay = progressStats?.currentDay || plan?.current_day || 1;
  const startDate = plan?.start_date ? new Date(plan.start_date) : new Date();
  const today = new Date();
  const daysElapsed = Math.max(1, Math.floor((today - startDate) / (1000 * 60 * 60 * 24)) + 1);
  const expectedProgress = Math.min(100, (daysElapsed / totalDays) * 100);
  const isAhead = overallCompletion >= expectedProgress;

  const getStreakColor = (streak) => {
    if (streak >= 30) return 'text-success';
    if (streak >= 14) return 'text-warning';
    if (streak >= 7) return 'text-accent';
    return 'text-muted-foreground';
  };

  const timeframeOptions = [
    { value: '3', label: 'Next 3 days' },
    { value: '7', label: 'Next 7 days' },
    { value: '14', label: 'Next 14 days' }
  ];

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto flex items-center justify-center py-12">
        <div className="text-center">
          <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-2" />
          <p className="text-muted-foreground">Loading progress data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Progress Header */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name="BarChart3" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">
                Progress Tracker
              </h2>
              <p className="text-muted-foreground text-sm">{plan?.reading_plans?.name}</p>
            </div>
          </div>
          <Button onClick={onBack} variant="outline" className="rounded-full">
            <Icon name="ArrowLeft" size={16} />
            Back
          </Button>
        </div>

        {/* Key Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard title="Current Day" value={currentDay} subtitle={`of ${totalDays}`} icon="Calendar" color="primary" />
          <StatCard title="Completion" value={`${overallCompletion?.toFixed(1)}%`} subtitle="Overall progress" icon="Target" color="success" />
          <StatCard title="Current Streak" value={progressStats?.currentStreak || 0} subtitle="days in a row" icon="Flame" color={getStreakColor(progressStats?.currentStreak || 0)?.replace('text-', '')} />
          <StatCard title="Missed Days" value={progressStats?.missedDays || 0} subtitle="since start" icon="AlertCircle" color="destructive" />
        </div>

        {/* Pace Graph */}
        <div className="bg-muted/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="TrendingUp" size={15} color="var(--color-primary)" />
              Reading Pace
            </h4>
            <span className={cn(
              "text-xs font-semibold px-2 py-0.5 rounded-full",
              isAhead ? "bg-success/15 text-success" : "bg-warning/15 text-warning"
            )}>
              {isAhead ? `${(overallCompletion - expectedProgress)?.toFixed(1)}% ahead` : `${(expectedProgress - overallCompletion)?.toFixed(1)}% behind`}
            </span>
          </div>
          <div className="relative w-full bg-muted rounded-full h-4 overflow-hidden">
            {/* Expected pace */}
            <div
              className="absolute top-0 left-0 h-4 rounded-full bg-muted-foreground/25 transition-all duration-500"
              style={{ width: `${Math.min(expectedProgress, 100)}%` }}
            />
            {/* Actual progress */}
            <div
              className={cn("absolute top-0 left-0 h-4 rounded-full transition-all duration-500", isAhead ? "bg-success" : "bg-primary")}
              style={{ width: `${Math.min(overallCompletion, 100)}%` }}
            />
            {/* Pace marker */}
            <div
              className="absolute top-0 h-4 w-0.5 bg-foreground/50"
              style={{ left: `${Math.min(expectedProgress, 100)}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
            <span>0%</span>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-2 rounded-sm bg-muted-foreground/30"></span>
                Expected ({expectedProgress?.toFixed(1)}%)
              </span>
              <span className="flex items-center gap-1">
                <span className={cn("inline-block w-3 h-2 rounded-sm", isAhead ? "bg-success" : "bg-primary")}></span>
                Actual ({overallCompletion?.toFixed(1)}%)
              </span>
            </div>
            <span>100%</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="border-b border-border px-5 py-4 bg-gradient-to-r from-primary/5 to-transparent">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentMonth(prev => Math.max(0, prev - 1))}
                disabled={currentMonth <= 0}
                className="p-1.5 rounded-lg hover:bg-muted/60 disabled:opacity-30 transition-colors"
              >
                <Icon name="ChevronLeft" size={16} color="var(--color-foreground)" />
              </button>
              <div className="text-center">
                <h3 className="text-base font-heading font-semibold text-foreground">{getMonthYear()}</h3>
                <p className="text-xs text-muted-foreground">{plan?.reading_plans?.name}</p>
              </div>
              <button
                onClick={() => setCurrentMonth(prev => prev + 1)}
                className="p-1.5 rounded-lg hover:bg-muted/60 transition-colors"
              >
                <Icon name="ChevronRight" size={16} color="var(--color-foreground)" />
              </button>
            </div>
          </div>

          <div className="p-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 mb-2">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']?.map(d => (
                <div key={d} className="text-center text-[11px] font-semibold text-muted-foreground py-1.5">
                  {d}
                </div>
              ))}
            </div>

            {calendarLoading ? (
              <div className="flex items-center justify-center py-8">
                <Icon name="Loader2" size={20} className="animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: getFirstDayOffset() })?.map((_, i) => (
                  <div key={`empty-${i}`} />
                ))}
                {calendarData?.map((day, index) => {
                  const isCompleted = completionStatus?.[day?.planDay];
                  const isToday = day?.isToday;
                  const isPast = day?.isPast && !isCompleted;
                  const isFuture = day?.isFuture;
                  const noDay = !day?.planDay;

                  return (
                    <div
                      key={index}
                      className={cn(
                        "aspect-square flex flex-col items-center justify-center rounded-lg text-xs font-medium transition-all duration-150 relative",
                        noDay && "opacity-0 pointer-events-none",
                        isCompleted && !isToday && "bg-success/20 text-success",
                        isToday && isCompleted && "bg-success text-white shadow-md ring-2 ring-success/40",
                        isToday && !isCompleted && "bg-primary text-primary-foreground shadow-md ring-2 ring-primary/30",
                        isPast && "bg-destructive/10 text-destructive/70",
                        isFuture && "bg-muted/40 text-muted-foreground hover:bg-muted/70 cursor-pointer"
                      )}
                    >
                      <span className="leading-none">{day?.monthDay}</span>
                      {isCompleted && (
                        <Icon name="Check" size={8} className="mt-0.5 opacity-80" />
                      )}
                      {isToday && !isCompleted && (
                        <span className="text-[8px] opacity-80 leading-none mt-0.5">now</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="border-t border-border px-4 py-3 bg-muted/20">
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11px]">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-success/20 border border-success/40 inline-block"></span>
                <span className="text-muted-foreground">Completed</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-primary inline-block"></span>
                <span className="text-muted-foreground">Today</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-destructive/10 border border-destructive/30 inline-block"></span>
                <span className="text-muted-foreground">Missed</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-muted/40 border border-border inline-block"></span>
                <span className="text-muted-foreground">Upcoming</span>
              </span>
            </div>
          </div>
        </div>

        {/* Reading Statistics */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-heading font-semibold text-foreground mb-5 flex items-center gap-2">
            <Icon name="BarChart2" size={18} color="var(--color-primary)" />
            Reading Statistics
          </h3>

          {readingStats ? (
            <div className="space-y-3">
              <StatRow label="Average reading time" value={readingStats?.averageTime} icon="Clock" />
              <StatRow label="Total time spent" value={readingStats?.totalTimeSpent} icon="Timer" />
              <StatRow label="Verses read" value={readingStats?.versesRead?.toLocaleString()} icon="FileText" />
              <StatRow label="Chapters completed" value={readingStats?.chaptersCompleted?.toLocaleString()} icon="BookOpen" />
              <StatRow label="Books started" value={readingStats?.booksStarted} icon="Book" />
              <StatRow label="Books completed" value={readingStats?.booksCompleted} icon="CheckCircle" />
            </div>
          ) : (
            <div className="space-y-3">
              {[
                { label: 'Average reading time', icon: 'Clock' },
                { label: 'Total time spent', icon: 'Timer' },
                { label: 'Verses read', icon: 'FileText' },
                { label: 'Chapters completed', icon: 'BookOpen' },
                { label: 'Books started', icon: 'Book' },
                { label: 'Books completed', icon: 'CheckCircle' }
              ]?.map(item => (
                <StatRow key={item?.label} label={item?.label} value="—" icon={item?.icon} />
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Upcoming Readings */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-foreground flex items-center gap-2">
            <Icon name="CalendarDays" size={18} color="var(--color-primary)" />
            Upcoming Readings
          </h3>
          <Select
            value={selectedTimeframe}
            onValueChange={setSelectedTimeframe}
            options={timeframeOptions}
          />
        </div>

        {upcomingReadings?.length > 0 ? (
          <div className="space-y-2">
            {upcomingReadings?.slice(0, parseInt(selectedTimeframe))?.map((reading, index) => (
              <div
                key={index}
                className={cn(
                  "flex items-center justify-between p-3 rounded-xl border transition-all",
                  index === 0
                    ? "bg-primary/5 border-primary/20" :"bg-muted/20 border-border hover:bg-muted/40"
                )}
              >
                <div className="flex items-center space-x-3 min-w-0">
                  <div className={cn(
                    "flex items-center justify-center w-8 h-8 rounded-lg shrink-0 text-xs font-semibold",
                    index === 0 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                  )}>
                    {index === 0 ? <Icon name="BookOpen" size={14} /> : index + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-foreground text-sm">
                      {reading?.date}
                      {reading?.title && <span className="text-muted-foreground font-normal"> · {reading?.title}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {reading?.passages?.join(', ') || 'No passages'}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground shrink-0 ml-2">
                  Day {reading?.dayNumber}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Calendar" size={32} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">No upcoming readings found</p>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, subtitle, icon, color = 'primary' }) => {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    destructive: 'bg-destructive/10 text-destructive',
    accent: 'bg-accent/10 text-accent'
  };

  return (
    <div className="text-center p-3 bg-muted/20 rounded-xl">
      <div className={cn("flex items-center justify-center w-10 h-10 rounded-lg mx-auto mb-2", colorClasses?.[color] || colorClasses?.primary)}>
        <Icon name={icon} size={18} />
      </div>
      <div className="font-heading font-bold text-lg text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground">{subtitle}</div>
      <div className="text-[11px] text-muted-foreground/70 mt-0.5">{title}</div>
    </div>
  );
};

const StatRow = ({ label, value, icon }) => (
  <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
    <div className="flex items-center space-x-2">
      <Icon name={icon} size={15} color="var(--color-muted-foreground)" />
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
    <span className="font-semibold text-foreground text-sm">{value}</span>
  </div>
);

export default ProgressTracker;